import React, { useState, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  Bell, 
  Database,
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';

interface LoggingSettings {
  retention: {
    userActivities: number; // days
    securityEvents: number; // days
    securityAlerts: number; // days
  };
  notifications: {
    emailEnabled: boolean;
    emailAddress: string;
    criticalAlerts: boolean;
    dailyDigest: boolean;
    weeklyReport: boolean;
  };
  monitoring: {
    realTimeEnabled: boolean;
    enableDetailedLogging: boolean;
    logPerformanceMetrics: boolean;
    anonymizeUserData: boolean;
  };
  storage: {
    compressionEnabled: boolean;
    archiveOldData: boolean;
    archiveAfterDays: number;
  };
}

const defaultSettings: LoggingSettings = {
  retention: {
    userActivities: 90,
    securityEvents: 365,
    securityAlerts: 180
  },
  notifications: {
    emailEnabled: true,
    emailAddress: '',
    criticalAlerts: true,
    dailyDigest: false,
    weeklyReport: true
  },
  monitoring: {
    realTimeEnabled: true,
    enableDetailedLogging: true,
    logPerformanceMetrics: false,
    anonymizeUserData: false
  },
  storage: {
    compressionEnabled: true,
    archiveOldData: true,
    archiveAfterDays: 730
  }
};

/**
 * Logging Settings Panel Component
 * 
 * Displays logging configuration and preferences
 */
export const LoggingSettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<LoggingSettings>(defaultSettings);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load settings from API (mock implementation)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // In a real implementation, this would load from the API
        // const response = await loggingApiService.getSettings();
        // setSettings(response);
        
        // For now, use default settings
        setSettings(defaultSettings);
      } catch (error) {
        console.error('Failed to load settings:', error);
        setError('Failed to load settings');
      }
    };

    loadSettings();
  }, []);

  const handleChange = (section: keyof LoggingSettings, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setUnsavedChanges(true);
    setSuccess(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    
    try {
      // In a real implementation, this would save to the API
      // await loggingApiService.updateSettings(settings);
      
      // Mock save delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUnsavedChanges(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      setError(error instanceof Error ? error.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings(defaultSettings);
    setUnsavedChanges(true);
    setSuccess(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Settings className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Logging Settings
          </h3>
        </div>

        <div className="flex items-center space-x-2">
          {unsavedChanges && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <AlertTriangle className="w-3 h-3" />
              <span>Unsaved Changes</span>
            </Badge>
          )}
          
          {success && (
            <Badge variant="default" className="flex items-center space-x-1">
              <CheckCircle className="w-3 h-3" />
              <span>Settings Saved</span>
            </Badge>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-600 font-medium">Error</p>
          </div>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Data Retention Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Database className="w-5 h-5 text-lex-accent mr-2" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Data Retention</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              User Activities Retention (days)
            </label>
            <Input
              type="number"
              min="1"
              max="3650"
              value={settings.retention.userActivities}
              onChange={(e) => handleChange('retention', 'userActivities', parseInt(e.target.value))}
              className="text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              How long to keep user activity logs
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Security Events Retention (days)
            </label>
            <Input
              type="number"
              min="1"
              max="3650"
              value={settings.retention.securityEvents}
              onChange={(e) => handleChange('retention', 'securityEvents', parseInt(e.target.value))}
              className="text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              How long to keep security event logs
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Security Alerts Retention (days)
            </label>
            <Input
              type="number"
              min="1"
              max="3650"
              value={settings.retention.securityAlerts}
              onChange={(e) => handleChange('retention', 'securityAlerts', parseInt(e.target.value))}
              className="text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              How long to keep security alerts
            </p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-lex-accent-soft rounded-lg">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-lex-accent mr-2 mt-0.5" />
            <div>
              <p className="text-sm text-lex-accent">
                <strong>Recommended:</strong> User activities (90 days), Security events (365 days), Security alerts (180 days)
              </p>
              <p className="text-xs text-lex-accent mt-1">
                Longer retention periods provide better audit trails but require more storage space.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Bell className="w-5 h-5 text-green-600 mr-2" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Notifications</h4>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Email Notifications
              </label>
              <p className="text-xs text-gray-500">
                Send notifications via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications.emailEnabled}
                onChange={(e) => handleChange('notifications', 'emailEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </div>

          {settings.notifications.emailEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                placeholder="admin@example.com"
                value={settings.notifications.emailAddress}
                onChange={(e) => handleChange('notifications', 'emailAddress', e.target.value)}
                className="text-sm"
              />
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.criticalAlerts}
                onChange={(e) => handleChange('notifications', 'criticalAlerts', e.target.checked)}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Critical Security Alerts (immediate notifications)
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.dailyDigest}
                onChange={(e) => handleChange('notifications', 'dailyDigest', e.target.checked)}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Daily Activity Digest
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.weeklyReport}
                onChange={(e) => handleChange('notifications', 'weeklyReport', e.target.checked)}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Weekly Security Report
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Monitoring Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Shield className="w-5 h-5 text-orange-600 mr-2" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Monitoring & Privacy</h4>
        </div>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Real-time Monitoring
              </span>
              <p className="text-xs text-gray-500">
                Enable live activity tracking and alerts
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.monitoring.realTimeEnabled}
                onChange={(e) => handleChange('monitoring', 'realTimeEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </label>

          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Detailed Logging
              </span>
              <p className="text-xs text-gray-500">
                Log additional metadata and context
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.monitoring.enableDetailedLogging}
                onChange={(e) => handleChange('monitoring', 'enableDetailedLogging', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </label>

          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Performance Metrics
              </span>
              <p className="text-xs text-gray-500">
                Track response times and system performance
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.monitoring.logPerformanceMetrics}
                onChange={(e) => handleChange('monitoring', 'logPerformanceMetrics', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </label>

          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Anonymize User Data
              </span>
              <p className="text-xs text-gray-500">
                Hash user identifiers for additional privacy
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.monitoring.anonymizeUserData}
                onChange={(e) => handleChange('monitoring', 'anonymizeUserData', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </label>
        </div>
      </div>

      {/* Storage Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Clock className="w-5 h-5 text-purple-600 mr-2" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Storage & Archiving</h4>
        </div>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Compression
              </span>
              <p className="text-xs text-gray-500">
                Compress old logs to save storage space
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.storage.compressionEnabled}
                onChange={(e) => handleChange('storage', 'compressionEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </label>

          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-Archive Old Data
              </span>
              <p className="text-xs text-gray-500">
                Automatically archive old logs for long-term storage
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.storage.archiveOldData}
                onChange={(e) => handleChange('storage', 'archiveOldData', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lex-accent/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-lex-accent"></div>
            </label>
          </label>

          {settings.storage.archiveOldData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Archive After (days)
              </label>
              <Input
                type="number"
                min="30"
                max="3650"
                value={settings.storage.archiveAfterDays}
                onChange={(e) => handleChange('storage', 'archiveAfterDays', parseInt(e.target.value))}
                className="text-sm max-w-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Move data to archive storage after this many days
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="outline"
          onClick={resetToDefaults}
          className="flex items-center space-x-2"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Reset to Defaults</span>
        </Button>

        <div className="flex items-center space-x-2">
          <Button
            onClick={saveSettings}
            disabled={!unsavedChanges || saving}
            className="flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}; 